import localforage from 'localforage';

/**
 * EPUB 파일 영구 저장 시스템 (IndexedDB)
 *
 * 문제: Blob URL은 새로고침 시 사라짐
 * 해결: ArrayBuffer를 IndexedDB에 영구 저장
 */

// IndexedDB 설정
const epubStore = localforage.createInstance({
  name: 'wia-books',
  storeName: 'epubs',
  description: 'EPUB files storage'
});

/**
 * EPUB 파일을 IndexedDB에 저장
 * @param bookId - 책 고유 ID
 * @param file - EPUB File 객체
 */
export async function saveEPUBFile(bookId: string, file: File): Promise<void> {
  try {
    console.log(`📥 EPUB 저장 시작: ${bookId}`);

    // File → ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    const sizeMB = (arrayBuffer.byteLength / 1024 / 1024).toFixed(2);

    // IndexedDB에 저장
    await epubStore.setItem(`epub-${bookId}`, arrayBuffer);

    console.log(`✅ EPUB 저장 완료: ${bookId} (${sizeMB}MB)`);
  } catch (error) {
    console.error(`❌ EPUB 저장 실패: ${bookId}`, error);
    throw new Error('EPUB 파일 저장에 실패했습니다.');
  }
}

/**
 * IndexedDB에서 EPUB 파일 로드
 * @param bookId - 책 고유 ID
 * @returns Blob 객체 또는 null
 */
export async function getEPUBFile(bookId: string): Promise<Blob | null> {
  try {
    console.log(`📤 EPUB 로드 시작: ${bookId}`);

    // IndexedDB에서 ArrayBuffer 로드
    const arrayBuffer = await epubStore.getItem<ArrayBuffer>(`epub-${bookId}`);

    if (!arrayBuffer) {
      console.warn(`⚠️ EPUB 파일 없음: ${bookId}`);
      return null;
    }

    // ArrayBuffer → Blob
    const blob = new Blob([arrayBuffer], { type: 'application/epub+zip' });
    const sizeMB = (blob.size / 1024 / 1024).toFixed(2);

    console.log(`✅ EPUB 로드 완료: ${bookId} (${sizeMB}MB)`);
    return blob;
  } catch (error) {
    console.error(`❌ EPUB 로드 실패: ${bookId}`, error);
    return null;
  }
}

/**
 * IndexedDB에서 EPUB 파일 삭제
 * @param bookId - 책 고유 ID
 */
export async function deleteEPUBFile(bookId: string): Promise<void> {
  try {
    console.log(`🗑️ EPUB 삭제 시작: ${bookId}`);

    await epubStore.removeItem(`epub-${bookId}`);

    console.log(`✅ EPUB 삭제 완료: ${bookId}`);
  } catch (error) {
    console.error(`❌ EPUB 삭제 실패: ${bookId}`, error);
  }
}

/**
 * 저장된 모든 EPUB 파일 목록 조회
 * @returns bookId 배열
 */
export async function listEPUBFiles(): Promise<string[]> {
  try {
    const keys = await epubStore.keys();
    return keys
      .filter(key => key.startsWith('epub-'))
      .map(key => key.replace('epub-', ''));
  } catch (error) {
    console.error('❌ EPUB 목록 조회 실패:', error);
    return [];
  }
}

/**
 * 전체 저장 용량 확인
 * @returns 저장된 총 용량 (bytes)
 */
export async function getStorageSize(): Promise<number> {
  try {
    const keys = await epubStore.keys();
    let totalSize = 0;

    for (const key of keys) {
      const arrayBuffer = await epubStore.getItem<ArrayBuffer>(key);
      if (arrayBuffer) {
        totalSize += arrayBuffer.byteLength;
      }
    }

    return totalSize;
  } catch (error) {
    console.error('❌ 저장 용량 확인 실패:', error);
    return 0;
  }
}

/**
 * 커버 이미지를 IndexedDB에 저장
 * @param bookId - 책 고유 ID
 * @param coverBlob - 커버 이미지 Blob
 */
export async function saveCoverImage(bookId: string, coverBlob: Blob): Promise<void> {
  try {
    console.log(`🖼️ 커버 이미지 저장 시작: ${bookId}`);

    // Blob → ArrayBuffer
    const arrayBuffer = await coverBlob.arrayBuffer();

    // IndexedDB에 저장
    await epubStore.setItem(`cover-${bookId}`, arrayBuffer);

    console.log(`✅ 커버 이미지 저장 완료: ${bookId}`);
  } catch (error) {
    console.error(`❌ 커버 이미지 저장 실패: ${bookId}`, error);
  }
}

/**
 * IndexedDB에서 커버 이미지 로드
 * @param bookId - 책 고유 ID
 * @returns Base64 Data URL 또는 null
 */
export async function getCoverImage(bookId: string): Promise<string | null> {
  try {
    const arrayBuffer = await epubStore.getItem<ArrayBuffer>(`cover-${bookId}`);

    if (!arrayBuffer) {
      return null;
    }

    // ArrayBuffer → Blob → Base64 Data URL
    const blob = new Blob([arrayBuffer]);
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`❌ 커버 이미지 로드 실패: ${bookId}`, error);
    return null;
  }
}

/**
 * IndexedDB에서 커버 이미지 삭제
 * @param bookId - 책 고유 ID
 */
export async function deleteCoverImage(bookId: string): Promise<void> {
  try {
    await epubStore.removeItem(`cover-${bookId}`);
    console.log(`✅ 커버 이미지 삭제 완료: ${bookId}`);
  } catch (error) {
    console.error(`❌ 커버 이미지 삭제 실패: ${bookId}`, error);
  }
}

/**
 * PDF 파일을 IndexedDB에 저장
 * @param bookId - 책 고유 ID
 * @param file - PDF File 객체
 */
export async function savePDFFile(bookId: string, file: File): Promise<void> {
  try {
    console.log(`📥 PDF 저장 시작: ${bookId}`);

    // File → ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    const sizeMB = (arrayBuffer.byteLength / 1024 / 1024).toFixed(2);

    // IndexedDB에 저장
    await epubStore.setItem(`pdf-${bookId}`, arrayBuffer);

    console.log(`✅ PDF 저장 완료: ${bookId} (${sizeMB}MB)`);
  } catch (error) {
    console.error(`❌ PDF 저장 실패: ${bookId}`, error);
    throw new Error('PDF 파일 저장에 실패했습니다.');
  }
}

/**
 * IndexedDB에서 PDF 파일 로드
 * @param bookId - 책 고유 ID
 * @returns ArrayBuffer 또는 null
 */
export async function getPDFFile(bookId: string): Promise<ArrayBuffer | null> {
  try {
    console.log(`📤 PDF 로드 시작: ${bookId}`);

    const arrayBuffer = await epubStore.getItem<ArrayBuffer>(`pdf-${bookId}`);

    if (!arrayBuffer) {
      console.warn(`⚠️ PDF 파일 없음: ${bookId}`);
      return null;
    }

    const sizeMB = (arrayBuffer.byteLength / 1024 / 1024).toFixed(2);
    console.log(`✅ PDF 로드 완료: ${bookId} (${sizeMB}MB)`);
    return arrayBuffer;
  } catch (error) {
    console.error(`❌ PDF 로드 실패: ${bookId}`, error);
    return null;
  }
}

/**
 * IndexedDB에서 PDF 파일 삭제
 * @param bookId - 책 고유 ID
 */
export async function deletePDFFile(bookId: string): Promise<void> {
  try {
    console.log(`🗑️ PDF 삭제 시작: ${bookId}`);
    await epubStore.removeItem(`pdf-${bookId}`);
    console.log(`✅ PDF 삭제 완료: ${bookId}`);
  } catch (error) {
    console.error(`❌ PDF 삭제 실패: ${bookId}`, error);
  }
}
